"use client";

import { MembersSideMenuNav } from "@/app/members/(use-shared-layout)/MembersSideMenuNav";
import { HorizontalLine } from "@/components/ui/HorizontalLine";
import { getMembersZendeskUrl } from "@/constants/zendesk-urls/members-zendesk-urls";
import { cn } from "@/lib/utils";
import type { MemberEligibility } from "@/server/models/member/member-eligibility";
import { membersZendeskSsoLink } from "@/server/models/zendesk/zendesk-sso-link";
import TextLogo from "@public/mykenpo-text-logo.svg";
import Link from "next/link";
import { type FC } from "react";

const memberEligibilityLabelForMember = {
  insured: "加入中",
  optional_and_continued: "任意継続中",
  lost_eligibility: "資格喪失",
} as Record<MemberEligibility, string>;

const TopElements: FC<{ actionRequiredApplicationsCount: number }> = ({
  actionRequiredApplicationsCount,
}) => {
  return (
    <div>
      <div className="ml-6 flex h-20 items-center">
        <Link href="/">
          <TextLogo />
        </Link>
      </div>
      <MembersSideMenuNav
        actionRequiredApplicationsCount={actionRequiredApplicationsCount}
      />
    </div>
  );
};

const BottomElements = ({
  relatedOffice,
  insuredNumber,
  branchNumber,
  memberEligibility,
}: {
  relatedOffice: { name: string; officeCode: string };
  insuredNumber: string;
  branchNumber: string;
  memberEligibility: MemberEligibility;
}) => {
  return (
    <div className="flex flex-col gap-6 p-6 text-sm">
      <div>
        <p className="mb-3 font-bold">{relatedOffice.name}</p>
        <div className="flex flex-col">
          <p>記号：{relatedOffice.officeCode}</p>
          <p>番号：{insuredNumber}</p>
          <p>枝番：{branchNumber}</p>
          <p>
            ステータス：{memberEligibilityLabelForMember[memberEligibility]}
          </p>
        </div>
      </div>
      <HorizontalLine />
      <div className="flex flex-col gap-2">
        <Link
          href={membersZendeskSsoLink(getMembersZendeskUrl("利用規約"))}
          prefetch={false}
          target="_blank"
          className="font-bold"
        >
          利用規約
        </Link>
        <Link
          href={membersZendeskSsoLink(getMembersZendeskUrl("個人情報保護方針"))}
          prefetch={false}
          target="_blank"
          className="font-bold"
        >
          個人情報保護方針
        </Link>
        <Link
          href={membersZendeskSsoLink(getMembersZendeskUrl("公告"))}
          prefetch={false}
          target="_blank"
          className="font-bold"
        >
          公告
        </Link>
      </div>
      <div className="whitespace-pre-wrap">
        {"©︎VCスタートアップ健保/\n©︎株式会社SalusTech"}
      </div>
    </div>
  );
};

export const sideMenuId = "side-menu";

export const MembersSideMenu = ({
  relatedOffice,
  insuredNumber,
  branchNumber,
  memberEligibility,
  sideMenuOpened,
  actionRequiredApplicationsCount,
}: {
  relatedOffice: { name: string; officeCode: string };
  insuredNumber: string;
  branchNumber: string;
  memberEligibility: MemberEligibility;
  sideMenuOpened: boolean;
  actionRequiredApplicationsCount: number;
}) => {
  return (
    <div
      id={sideMenuId}
      data-opened={sideMenuOpened}
      className={cn(
        "z-50 flex max-w-[232px] min-w-[232px] flex-col justify-between overflow-y-scroll bg-monotone-0.5 transition-transform",
        // ヘッダ分を抜いた高さ
        "fixed top-[80px] h-[calc(100%-80px)]",
        // 画面外に隠しておいて表示するときにずらす
        "left-[-232px] data-[opened=true]:translate-x-[232px]",
        // 一定幅以上の場合は常にサイドメニューを表示
        "lg:sticky lg:top-0 lg:h-screen",
      )}
    >
      <TopElements
        actionRequiredApplicationsCount={actionRequiredApplicationsCount}
      />
      <BottomElements
        relatedOffice={relatedOffice}
        insuredNumber={insuredNumber}
        branchNumber={branchNumber}
        memberEligibility={memberEligibility}
      />
    </div>
  );
};
